'use client'

import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useNavigate, useSearchParams, Navigate } from 'react-router-dom'
import { motion, AnimatePresence } from 'framer-motion'
import ReactMarkdown from 'react-markdown'
import remarkGfm from 'remark-gfm'
import {
    Send, X, BookOpen, ChevronRight, ChevronDown, ChevronUp, Zap, Award,
    ArrowLeft, CheckCircle, Circle, Lock, Menu, GraduationCap,
    Target, Sparkles, Clock, BarChart3, Loader2, MoreVertical, Info, LayoutList,
    ShieldCheck, Users, Palette
} from 'lucide-react'
import { PREP_CATEGORY_IDS } from '@/utils/curriculum'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@/hooks/useAuth'
import { useEducationSession, EduMessage } from '@/hooks/useEducationSession'
import { createClientComponentClient } from '@/integrations/supabase/client'
import QuizInterface from '@/components/education/QuizInterface'
import SafeVideoPlayer from '@/components/video/SafeVideoPlayer'
import LanguagePicker from '@/components/ui/LanguagePicker'
import { ModuleMilestone } from '@/components/education/ModuleMilestone'
import { ShortAnswerBox } from '@/components/education/ShortAnswerBox'
import { generateReflectionPrompt, getReflectionWordLimits } from '@/utils/reflectionPrompt'
import { getEntitlements } from '@/utils/entitlements'

// ─── Design Tokens ─────────────────────────────────────────────────────────────
const T = {
    // Core palette
    sage: '#4a7c5e',
    sageDark: '#2d5a3d',
    sageLight: '#e8f2ec',
    sageMid: '#b8d9c4',
    cream: '#faf7f2',
    creamDark: '#f0ebe0',
    navy: '#1a2740',
    navyLight: '#243452',
    slate: '#4a5568',
    white: '#ffffff',
    // Education accents
    studyBlue: '#3b5bdb',
    studyBlueDark: '#2d44b8',
    studyBlueLight: '#e8edfc',
    gold: '#c9903a',
    goldLight: '#fdf6e9',
    error: '#ef4444',
    success: '#22c55e',
    successLight: '#f0fdf4',
    // Typography
    fontDisplay: "'Playfair Display', Georgia, serif",
    fontBody: "'Source Serif 4', Georgia, serif",
    fontMono: "'JetBrains Mono', 'Fira Code', monospace",
}

// ─── Theme Definitions ─────────────────────────────────────────────────────────
export type SessionTheme = 'university' | 'obsidian' | 'parchment'

export const SESSION_THEMES: Record<SessionTheme, {
    name: string
    outerBg: string
    headerBg: string
    aiBubbleBg: string
    aiBubbleBorder: string
    aiTextColor: string
    userBubbleBg: string
    userBubbleBorder: string
    userTextColor: string
    chipBg: string
    chipBorder: string
    chipTextColor: string
    chipHoverBg: string
    chipHoverBorder: string
    chipHoverTextColor: string
    inputBg: string
    inputBorder: string
    inputTextColor: string
    inputPlaceholder: string
    objectiveBg: string
    objectiveBorder: string
    objectiveLabelColor: string
    objectiveTextColor: string
    proseColor: string
    proseHeadingColor: string
    proseStrongColor: string
}> = {
    university: {
        name: 'University Dark Glass',
        outerBg: '#0d1520',
        headerBg: '#111827',
        aiBubbleBg: '#162235',
        aiBubbleBorder: '1px solid rgba(255,255,255,0.10)',
        aiTextColor: '#f8fafc',
        userBubbleBg: 'rgba(74,124,94,0.22)',
        userBubbleBorder: '1px solid rgba(184,217,196,0.25)',
        userTextColor: '#f8fafc',
        chipBg: '#1e293b',
        chipBorder: 'rgba(255,255,255,0.12)',
        chipTextColor: '#e2e8f0',
        chipHoverBg: '#24344d',
        chipHoverBorder: '#c9903a',
        chipHoverTextColor: '#ffffff',
        inputBg: '#1e293b',
        inputBorder: 'rgba(255,255,255,0.15)',
        inputTextColor: '#f8fafc',
        inputPlaceholder: 'rgba(226,232,240,0.45)',
        objectiveBg: 'rgba(74,124,94,0.18)',
        objectiveBorder: '1px solid rgba(74,124,94,0.35)',
        objectiveLabelColor: '#86efac',
        objectiveTextColor: '#f8fafc',
        proseColor: '#f8fafc',
        proseHeadingColor: '#ffffff',
        proseStrongColor: '#ffffff',
    },
    obsidian: {
        name: 'Obsidian & Gold',
        outerBg: '#07090e',
        headerBg: '#0d1117',
        aiBubbleBg: '#141b26',
        aiBubbleBorder: '1px solid rgba(212,168,67,0.25)',
        aiTextColor: '#f8f6f0',
        userBubbleBg: 'rgba(212,168,67,0.18)',
        userBubbleBorder: '1px solid rgba(212,168,67,0.35)',
        userTextColor: '#fef08a',
        chipBg: '#161f30',
        chipBorder: 'rgba(212,168,67,0.22)',
        chipTextColor: '#f3eed9',
        chipHoverBg: '#232f45',
        chipHoverBorder: '#d4a843',
        chipHoverTextColor: '#ffffff',
        inputBg: '#141b26',
        inputBorder: 'rgba(212,168,67,0.25)',
        inputTextColor: '#f8f6f0',
        inputPlaceholder: 'rgba(243,238,217,0.45)',
        objectiveBg: 'rgba(212,168,67,0.12)',
        objectiveBorder: '1px solid rgba(212,168,67,0.3)',
        objectiveLabelColor: '#d4a843',
        objectiveTextColor: '#fef08a',
        proseColor: '#f8f6f0',
        proseHeadingColor: '#fef08a',
        proseStrongColor: '#ffffff',
    },
    parchment: {
        name: 'Warm Parchment',
        outerBg: '#181615',
        headerBg: '#24201e',
        aiBubbleBg: '#eae5da',
        aiBubbleBorder: '1px solid #d6cfc4',
        aiTextColor: '#1c1917',
        userBubbleBg: '#243452',
        userBubbleBorder: '1px solid rgba(255,255,255,0.15)',
        userTextColor: '#f8fafc',
        chipBg: '#f2ece1',
        chipBorder: '1px solid #d6cfc4',
        chipTextColor: '#44403c',
        chipHoverBg: '#e5dec9',
        chipHoverBorder: '#c9903a',
        chipHoverTextColor: '#1c1917',
        inputBg: '#24201e',
        inputBorder: 'rgba(255,255,255,0.15)',
        inputTextColor: '#f8fafc',
        inputPlaceholder: 'rgba(226,232,240,0.45)',
        objectiveBg: 'rgba(212,168,67,0.15)',
        objectiveBorder: '1px solid rgba(212,168,67,0.3)',
        objectiveLabelColor: '#d4a843',
        objectiveTextColor: '#fef08a',
        proseColor: '#1c1917',
        proseHeadingColor: '#0f172a',
        proseStrongColor: '#0f172a',
    }
}

// ─── Types ─────────────────────────────────────────────────────────────────────
interface LessonPlan {
    module_id: number
    title: string
    lessons: {
        lesson_id: number
        title: string
        learning_objective: string
        passing_score: number
        core_concepts: string[]
    }[]
}

interface SubjectProgressData {
    current_unit: number
    current_lesson: number
    mastery_score: number
    ai_generated_path: LessonPlan[] | null
    lessons_completed: string[]
}

// ─── Safe Media Components (hide on error) ──────────────────────────────────
function SafeMediaImage({ url, caption }: { url: string; caption?: string }) {
    const [failed, setFailed] = useState(false);
    if (failed) return null;
    return (
        <div style={{
            margin: '16px 0', borderRadius: 14, overflow: 'hidden',
            border: `1px solid ${T.creamDark}`, background: T.cream
        }}>
            <img
                src={url}
                alt={caption || 'Lesson visual'}
                style={{ width: '100%', maxHeight: 360, objectFit: 'contain', display: 'block' }}
                loading="lazy"
                onError={() => setFailed(true)}
            />
            {caption && (
                <p style={{ padding: '10px 14px', fontSize: 12, color: T.slate, margin: 0, fontStyle: 'italic' }}>
                    {caption}
                </p>
            )}
        </div>
    );
}

function SafeMediaVideo({ url, caption }: { url: string; caption?: string }) {
    const [failed, setFailed] = useState(false);
    if (failed) return null;
    return (
        <div style={{ margin: '16px 0', borderRadius: 14, overflow: 'hidden' }}>
            <SafeVideoPlayer
                videoUrl={url}
                title={caption || 'Lesson video'}
                className="w-full aspect-video"
                onError={() => setFailed(true)}
            />
        </div>
    );
}

// ─── Media Parser ──────────────────────────────────────────────────────────────
function parseMediaContent(content: string): React.ReactNode[] {
    const parts: React.ReactNode[] = []
    const regex = /\[(IMAGE|VIDEO):\s*([^\]|]+?)(?:\s*\|\s*(?:Caption:\s*"?)?([^"\]]*)?"?)?\]/g
    let lastIndex = 0
    let match: RegExpExecArray | null

    while ((match = regex.exec(content)) !== null) {
        if (match.index > lastIndex) {
            parts.push(
                <ReactMarkdown key={`t-${lastIndex}`} remarkPlugins={[remarkGfm]}>
                    {content.slice(lastIndex, match.index)}
                </ReactMarkdown>
            )
        }
        const type = match[1]
        const url = match[2].trim()
        const caption = match[3]?.trim() || ''
        if (type === 'IMAGE') {
            parts.push(<SafeMediaImage key={`img-${match.index}`} url={url} caption={caption} />)
        } else if (type === 'VIDEO') {
            parts.push(<SafeMediaVideo key={`vid-${match.index}`} url={url} caption={caption} />)
        }
        lastIndex = match.index + match[0].length
    }

    if (lastIndex < content.length) {
        parts.push(
            <ReactMarkdown key="t-end" remarkPlugins={[remarkGfm]}>
                {content.slice(lastIndex)}
            </ReactMarkdown>
        )
    }

    return parts.length > 0 ? parts : [<ReactMarkdown key="full" remarkPlugins={[remarkGfm]}>{content}</ReactMarkdown>]
}

// ─── Mastery Ring SVG ──────────────────────────────────────────────────────────
function MasteryRing({ score, size = 44 }: { score: number; size?: number }) {
    const r = (size / 2) - 4
    const circ = 2 * Math.PI * r
    const offset = circ - (score / 100) * circ
    const clr = score >= 90 ? T.gold : score >= 70 ? T.sage : score >= 40 ? T.studyBlue : T.sageMid

    return (
        <svg width={size} height={size} style={{ flexShrink: 0 }}>
            <circle cx={size / 2} cy={size / 2} r={r} fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="3" />
            <motion.circle
                cx={size / 2} cy={size / 2} r={r}
                fill="none" stroke={clr} strokeWidth="3" strokeLinecap="round"
                strokeDasharray={circ} strokeDashoffset={offset}
                transform={`rotate(-90 ${size / 2} ${size / 2})`}
                initial={{ strokeDashoffset: circ }}
                animate={{ strokeDashoffset: offset }}
                transition={{ duration: 1.2, ease: 'easeOut' }}
            />
            <text x={size / 2} y={size / 2 + 4} textAnchor="middle" fill="white" fontSize="10" fontWeight="800" fontFamily="system-ui">
                {Math.round(score)}%
            </text>
        </svg>
    )
}

// ─── Message Bubble ────────────────────────────────────────────────────────────
const MessageBubble = React.memo(function MessageBubble({ message, theme = 'university' }: { message: EduMessage; theme?: SessionTheme }) {
    const { t } = useTranslation()
    const isAI = message.role === 'assistant'
    const th = SESSION_THEMES[theme] || SESSION_THEMES.university

    const isQuizResult = (message.metadata as Record<string, unknown>)?.quiz_result
    const hasMedia = isAI && /\[(IMAGE|VIDEO):/.test(message.content)

    return (
        <motion.div
            id={isAI ? `msg-${message.id}` : undefined}
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            style={{
                display: 'flex', justifyContent: isAI ? 'flex-start' : 'flex-end',
                marginBottom: 20, gap: 12, alignItems: 'flex-start'
            }}
        >
            {isAI && (
                <div style={{
                    width: 36, height: 36, borderRadius: '50%', flexShrink: 0,
                    background: `linear-gradient(135deg, ${T.studyBlue}, ${T.studyBlueDark})`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    boxShadow: '0 4px 12px rgba(59,91,219,0.25)', marginTop: 2
                }}>
                    <img src="/icons/blue-sage-logo.png" alt="Sage" style={{
                        width: 22, height: 22, filter: 'brightness(0) invert(1)'
                    }} />
                </div>
            )}

            <div className="edu-bubble" style={{
                maxWidth: '82%', padding: '16px 20px',
                borderRadius: isAI ? '4px 20px 20px 20px' : '20px 20px 4px 20px',
                background: isQuizResult ? 'rgba(34,197,94,0.15)'
                    : isAI ? th.aiBubbleBg : th.userBubbleBg,
                color: isAI ? th.aiTextColor : th.userTextColor,
                border: isQuizResult ? '1px solid rgba(34,197,94,0.3)'
                    : isAI ? th.aiBubbleBorder : th.userBubbleBorder,
                boxShadow: isAI
                    ? '0 2px 12px rgba(0,0,0,0.06)'
                    : '0 4px 16px rgba(59,91,219,0.25)',
                fontSize: 16, lineHeight: 1.7
            }}>
                {isQuizResult ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: '#166534', fontWeight: 700 }}>
                            <CheckCircle size={18} /><span>{t('education.quiz_results')}</span>
                        </div>
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
                    </div>
                ) : hasMedia ? (
                    <div className="prose-sage">{parseMediaContent(message.content)}</div>
                ) : (
                    <div className="prose-sage">
                        <ReactMarkdown remarkPlugins={[remarkGfm]}>{message.content}</ReactMarkdown>
                    </div>
                )}
                {Boolean((message.metadata as any)?.guide_mode || (message.metadata as any)?.parent_guide) && (
                    <div className="mt-3 rounded-xl border p-3"
                        style={{ background: '#FBF6EA', borderColor: '#E8D9AE' }}>
                        <div className="text-[9px] font-black uppercase tracking-wider mb-1 flex items-center gap-1"
                            style={{ color: '#A8842C' }}>
                            <Users size={10} /> For you — guide this step
                        </div>
                        <p className="text-[12.5px] font-medium" style={{ color: '#1B2437' }}>
                            {String((message.metadata as any)?.guide_mode || (message.metadata as any)?.parent_guide)}
                        </p>
                    </div>
                )}
                <div style={{
                    fontSize: 10, marginTop: 8, textAlign: 'right',
                    opacity: 0.6, color: '#f8fafc'
                }}>
                    {new Date(message.created_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
            </div>

            {!isAI && (
                <div style={{
                    width: 36, height: 36, borderRadius: 12, flexShrink: 0,
                    background: `linear-gradient(135deg, ${T.gold}, #b8802e)`,
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    color: T.white, fontWeight: 700, fontSize: 14, marginTop: 2,
                    boxShadow: '0 4px 12px rgba(201,144,58,0.25)'
                }}>U</div>
            )}
        </motion.div>
    )
})


// ─── Typing Indicator ──────────────────────────────────────────────────────────
function TypingIndicator() {
    return (
        <motion.div
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            style={{
                display: 'flex', alignItems: 'center', gap: 12,
                marginBottom: 20, paddingLeft: 48
            }}
        >
            <div style={{
                padding: '14px 20px', borderRadius: '4px 20px 20px 20px',
                background: T.white, border: `1px solid ${T.creamDark}`,
                display: 'flex', gap: 4
            }}>
                {[0, 0.15, 0.3].map((d, i) => (
                    <motion.div
                        key={i}
                        style={{ width: 8, height: 8, borderRadius: '50%', background: T.studyBlue }}
                        animate={{ y: [0, -6, 0], opacity: [0.4, 1, 0.4] }}
                        transition={{ duration: 1, delay: d, repeat: Infinity, ease: 'easeInOut' }}
                    />
                ))}
            </div>
        </motion.div>
    )
}

// ─── Curriculum Sidebar ────────────────────────────────────────────────────────
function CurriculumSidebar({
    plan, currentUnit, currentLesson, masteryScore, categoryName,
    categoryIcon, isOpen, onClose, lessonsCompleted,
    currentConcepts = [],
    currentConceptIndex = 0,
    showForceControl = false,
    onForceNextConcept
}: {
    plan: LessonPlan[]
    currentUnit: number
    currentLesson: number
    masteryScore: number
    categoryName: string
    categoryIcon: string
    isOpen: boolean
    onClose: () => void
    lessonsCompleted: string[]
    currentConcepts?: string[]
    currentConceptIndex?: number
    showForceControl?: boolean
    onForceNextConcept?: () => void
}) {
    const { t } = useTranslation()

    const totalLessons = plan.reduce((sum, m) => sum + m.lessons.length, 0)
    const completedCount = lessonsCompleted.length

    return (
        <>
            {/* Mobile overlay backdrop */}
            <AnimatePresence>
                {isOpen && (
                    <motion.div
                        className="edu-sidebar-backdrop"
                        style={{
                            position: 'fixed', inset: 0, background: 'rgba(26,39,64,0.5)',
                            zIndex: 89, display: 'none'
                        }}
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                        onClick={onClose}
                    />
                )}
            </AnimatePresence>

            <motion.aside
                className="edu-sidebar"
                style={{
                    width: 320, maxWidth: '100vw', flexShrink: 0, height: '100%',
                    background: `linear-gradient(180deg, ${T.navy} 0%, #1e3048 100%)`,
                    borderRight: '1px solid rgba(184,217,196,0.08)',
                    display: 'flex', flexDirection: 'column',
                    overflow: 'hidden',
                    position: isOpen ? 'relative' : 'relative',
                }}
                initial={false}
                animate={{ marginLeft: isOpen ? 0 : -320, opacity: isOpen ? 1 : 0 }}
                transition={{ type: 'spring', stiffness: 300, damping: 30 }}
            >
                {/* Subject header */}
                <div style={{
                    padding: '20px 20px 16px',
                    borderBottom: '1px solid rgba(255,255,255,0.06)',
                }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                            <div style={{
                                width: 40, height: 40, borderRadius: 12,
                                background: 'rgba(59,91,219,0.2)',
                                border: '1px solid rgba(59,91,219,0.3)',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                fontSize: 20
                            }}>{categoryIcon}</div>
                            <div>
                                <div style={{
                                    fontFamily: T.fontDisplay, fontSize: 15, fontWeight: 700,
                                    color: T.white, lineHeight: 1.2
                                }}>{categoryName}</div>
                                <div style={{ fontSize: 11, color: T.sageMid, marginTop: 2 }}>
                                    {t('education.session.learningPath', 'Learning Path')}
                                </div>
                            </div>
                        </div>
                        <button onClick={onClose} style={{
                            width: 32, height: 32, borderRadius: 8, border: 'none',
                            background: 'rgba(255,255,255,0.06)', cursor: 'pointer',
                            color: 'rgba(255,255,255,0.5)', display: 'flex',
                            alignItems: 'center', justifyContent: 'center'
                        }}>
                            <X size={16} />
                        </button>
                    </div>

                    {/* Progress stats */}
                    <div style={{
                        display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(min(100%, 110px), 1fr))', gap: 8
                    }}>
                        {[
                            { icon: <Target size={12} />, label: 'Mastery', value: `${Math.round(masteryScore)}%` },
                            { icon: <BarChart3 size={12} />, label: 'Done', value: `${completedCount}/${totalLessons}` },
                            { icon: <Sparkles size={12} />, label: 'Score', value: masteryScore >= 90 ? '⭐' : masteryScore >= 70 ? '👍' : '📖' },
                        ].map((stat, i) => (
                            <div key={i} style={{
                                padding: '8px 10px', borderRadius: 10,
                                background: 'rgba(255,255,255,0.04)',
                                border: '1px solid rgba(255,255,255,0.06)',
                                textAlign: 'center'
                            }}>
                                <div style={{ color: T.sageMid, display: 'flex', justifyContent: 'center', marginBottom: 4 }}>{stat.icon}</div>
                                <div style={{ fontSize: 13, fontWeight: 700, color: T.white }}>{stat.value}</div>
                                <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>{stat.label}</div>
                            </div>
                        ))}
                    </div>
                </div>

                {/* Current Lesson Concept Checklist */}
                {currentConcepts.length > 0 && (
                    <div style={{
                        padding: '12px 16px',
                        borderBottom: '1px solid rgba(255,255,255,0.06)',
                        background: 'rgba(0,0,0,0.12)'
                    }}>
                        <div style={{
                            fontSize: 10, textTransform: 'uppercase', letterSpacing: '0.08em',
                            color: T.sageMid, marginBottom: 8, fontWeight: 700
                        }}>
                            This Lesson · {currentConcepts.length} concepts
                        </div>
                        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                            {currentConcepts.map((c, i) => (
                                <div key={i} style={{
                                    display: 'flex', gap: 8, alignItems: 'center', padding: '3px 0',
                                    fontSize: 11, fontWeight: i === currentConceptIndex ? 700 : 400,
                                    color: i < currentConceptIndex ? T.success
                                         : i === currentConceptIndex ? T.white : 'rgba(255,255,255,0.35)'
                                }}>
                                    {i < currentConceptIndex ? (
                                        <CheckCircle size={12} style={{ color: T.success, flexShrink: 0 }} />
                                    ) : i === currentConceptIndex ? (
                                        <span style={{ color: T.studyBlue, fontSize: 13, lineHeight: '12px', flexShrink: 0 }}>●</span>
                                    ) : (
                                        <Circle size={12} style={{ color: 'rgba(255,255,255,0.25)', flexShrink: 0 }} />
                                    )}
                                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{c}</span>
                                </div>
                            ))}
                        </div>
                        {showForceControl && onForceNextConcept && (
                            <button
                                onClick={onForceNextConcept}
                                style={{
                                    marginTop: 10, width: '100%', padding: '7px 10px', borderRadius: 8,
                                    background: 'rgba(212,168,67,0.15)', border: '1px solid rgba(212,168,67,0.35)',
                                    color: '#fef08a', fontSize: 11, fontWeight: 700, cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                                    transition: 'background 0.2s'
                                }}
                            >
                                <Sparkles size={12} />
                                {currentConceptIndex >= currentConcepts.length - 1 ? 'Finish lesson →' : 'Skip to next concept →'}
                            </button>
                        )}
                    </div>
                )}

                {/* Lesson tree */}
                <div style={{ flex: 1, overflowY: 'auto', padding: '12px 16px' }}>
                    {plan.map((mod, modIdx) => (
                        <ModuleNode key={mod.module_id || modIdx} module={mod}
                            modIdx={modIdx}
                            currentUnit={currentUnit} currentLesson={currentLesson}
                            currentModuleIdx={Math.min(currentUnit, plan.length) - 1}
                            lessonsCompleted={lessonsCompleted}
                        />
                    ))}
                </div>
            </motion.aside>

            {/* Responsive styles */}
            <style>{`
        @media (max-width: 1023px) {
          .edu-sidebar { position: fixed !important; left: 0; top: 0; bottom: 0; height: 100% !important; height: 100dvh !important; z-index: 90 !important; }
          .edu-sidebar-backdrop { display: block !important; }
        }
        @media (max-width: 767px) {
          .edu-sidebar { width: min(300px, 85vw) !important; }
        }
      `}</style>
        </>
    )
}

function ModuleNode({
    module: mod, modIdx, currentUnit, currentLesson, currentModuleIdx, lessonsCompleted
}: {
    module: LessonPlan
    modIdx: number
    currentUnit: number
    currentLesson: number
    currentModuleIdx: number
    lessonsCompleted: string[]
}) {
    const isCurrentModule = mod.module_id === currentUnit || modIdx === currentModuleIdx
    const [expanded, setExpanded] = useState(isCurrentModule)

    useEffect(() => {
        if (isCurrentModule) setExpanded(true)
    }, [isCurrentModule])

    return (
        <div style={{ marginBottom: 8 }}>
            <button onClick={() => setExpanded(!expanded)} style={{
                width: '100%', textAlign: 'left', padding: '10px 12px', borderRadius: 10,
                border: 'none', cursor: 'pointer',
                background: isCurrentModule ? 'rgba(59,91,219,0.12)' : 'transparent',
                display: 'flex', alignItems: 'center', gap: 8,
                transition: 'background 0.2s'
            }}>
                <motion.div animate={{ rotate: expanded ? 90 : 0 }} style={{ color: T.sageMid, flexShrink: 0 }}>
                    <ChevronRight size={14} />
                </motion.div>
                <GraduationCap size={14} style={{ color: isCurrentModule ? T.studyBlue : T.sageMid, flexShrink: 0 }} />
                <span style={{
                    fontSize: 13, fontWeight: 600, flex: 1,
                    color: isCurrentModule ? T.white : 'rgba(255,255,255,0.6)',
                    overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                }}>{mod.title}</span>
            </button>

            <AnimatePresence>
                {expanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        style={{ overflow: 'hidden', paddingLeft: 24 }}
                    >
                        {mod.lessons.map((lesson, lesIdx) => {
                            const isCurrent = (Number(mod.module_id) === Number(currentUnit) && (Number(lesson.lesson_id) === Number(currentLesson) || String(lesson.lesson_id) === String(currentLesson))) ||
                                (isCurrentModule && (lesIdx === currentLesson - 1 || Number(lesson.lesson_id) === Number(currentLesson) || String(lesson.lesson_id) === String(currentLesson)))
                            const isCompleted = lessonsCompleted.includes(`${mod.module_id}-${lesson.lesson_id}`) ||
                                lessonsCompleted.includes(`u${mod.module_id}_l${lesson.lesson_id}`) ||
                                lessonsCompleted.includes(String(lesson.lesson_id)) ||
                                lessonsCompleted.includes(`${modIdx + 1}-${lesIdx + 1}`)

                            const isPast = (modIdx < currentModuleIdx) ||
                                (isCurrentModule && lesIdx < currentLesson - 1)
                            const isFuture = !isCurrent && !isPast && !isCompleted

                            return (
                                <div key={lesson.lesson_id} style={{
                                    display: 'flex', alignItems: 'flex-start', gap: 10,
                                    padding: '8px 10px', borderRadius: 8,
                                    background: isCurrent ? 'rgba(59,91,219,0.08)' : 'transparent',
                                    marginBottom: 2
                                }}>
                                    <div style={{ marginTop: 2, flexShrink: 0 }}>
                                        {isPast || isCompleted ? (
                                            <CheckCircle size={14} style={{ color: T.success }} />
                                        ) : isCurrent ? (
                                            <motion.div
                                                animate={{ scale: [1, 1.2, 1] }}
                                                transition={{ duration: 2, repeat: Infinity }}
                                            >
                                                <Circle size={14} style={{ color: T.studyBlue, fill: T.studyBlue }} />
                                            </motion.div>
                                        ) : (
                                            <Lock size={12} style={{ color: 'rgba(255,255,255,0.2)' }} />
                                        )}
                                    </div>
                                    <div style={{ flex: 1, minWidth: 0 }}>
                                        <div style={{
                                            fontSize: 12, fontWeight: isCurrent ? 700 : 500,
                                            color: isFuture ? 'rgba(255,255,255,0.3)' : isCurrent ? T.white : 'rgba(255,255,255,0.65)',
                                            overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                                        }}>
                                            {lesson.title}
                                        </div>
                                        {isCurrent && (
                                            <div style={{ fontSize: 10, color: T.studyBlue, marginTop: 2, fontWeight: 600 }}>
                                                ● In Progress
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )
                        })}
                    </motion.div>
                )}
            </AnimatePresence>
        </div>
    )
}


// ─── Quiz Preparing Indicator ─────────────────────────────────────────────────
function QuizPreparing() {
    const lines = ["Reading today's lesson…", "Choosing the right questions…", "Writing the final touches…"]
    const [line, setLine] = useState(0)
    useEffect(() => {
        const id = setInterval(() => setLine(l => (l + 1) % lines.length), 3500)
        return () => clearInterval(id)
    }, [lines.length])

    return (
        <div style={{
            position: 'fixed', inset: 0, zIndex: 100,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            background: 'rgba(26,39,64,0.55)', backdropFilter: 'blur(3px)'
        }}>
            <motion.div
                initial={{ scale: 0.92, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0.92, opacity: 0 }}
                style={{
                    background: T.white, borderRadius: 20, padding: '32px 40px',
                    textAlign: 'center', maxWidth: 360, margin: '0 16px',
                    boxShadow: '0 20px 40px rgba(0,0,0,0.2)'
                }}
            >
                <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1.6, repeat: Infinity, ease: 'linear' }}
                    style={{
                        width: 44, height: 44, margin: '0 auto 14px', borderRadius: '50%',
                        border: `3px solid ${T.creamDark}`, borderTopColor: T.studyBlue
                    }}
                />
                <div style={{ fontFamily: T.fontDisplay, fontSize: 18, fontWeight: 700, color: T.navy }}>
                    Preparing your quiz
                </div>
                <motion.div
                    key={line}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                    style={{ fontSize: 13, color: T.slate, marginTop: 6, minHeight: 18 }}
                >
                    {lines[line]}
                </motion.div>
                <div style={{ fontSize: 11, color: T.sage, marginTop: 10 }}>
                    Sage writes each quiz just for you — about 15 seconds.
                </div>
            </motion.div>
        </div>
    )
}

// ═══════════════════════════════════════════════════════════════════════════════
// ─── MAIN PAGE COMPONENT ────────────────────────────────────────────────────
// ═══════════════════════════════════════════════════════════════════════════════

export default function EducationSessionPage() {
    const navigate = useNavigate()
    const { t } = useTranslation()
    const [searchParams] = useSearchParams()
    const { user, userProfile, isLoading: authLoading, updateProfile } = useAuth()
    // Stabilise supabase ref — createClientComponentClient() returns a new object
    // each call. An unstabilised reference in the loadMeta dep array causes it to
    // re-fire on every auth refresh (including the webhook-race 10 s refresh),
    // which sets loadingMeta: true mid-session and resets the scroll position.
    const supabaseRef = useRef(createClientComponentClient())
    const supabase = supabaseRef.current

    // URL params
    const categoryId = searchParams.get('category') || ''
    const classId = searchParams.get('class') || null

    // Category metadata
    const [categoryName, setCategoryName] = useState('')
    const [categoryIcon, setCategoryIcon] = useState('📚')
    const [loadingMeta, setLoadingMeta] = useState(true)

    // Lesson plan data
    const [lessonPlan, setLessonPlan] = useState<LessonPlan[]>([])
    const [lessonsCompleted, setLessonsCompleted] = useState<string[]>([])

    // UI state
    const [sidebarOpen, setSidebarOpen] = useState(false)
    const [userInput, setUserInput] = useState('')
    const [sessionSeconds, setSessionSeconds] = useState(0)
    const [showExitConfirm, setShowExitConfirm] = useState(false)
    const [hasAutoStarted, setHasAutoStarted] = useState(false)
    const [showMilestone, setShowMilestone] = useState(false)
    const [essayMode, setEssayMode] = useState<string | null>(null)
    const [essayTrack, setEssayTrack] = useState<string | null>(null)
    const [showReflection, setShowReflection] = useState(false)
    const [reflectionPrompt, setReflectionPrompt] = useState('')
    const [prevCurrentUnit, setPrevCurrentUnit] = useState<number | null>(null)

    // Theme state
    const [theme, setTheme] = useState<SessionTheme>(() => {
        try {
            return (localStorage.getItem('edu_session_theme') as SessionTheme) || 'university'
        } catch {
            return 'university'
        }
    })
    const currentTheme = SESSION_THEMES[theme] || SESSION_THEMES.university

    const cycleTheme = () => {
        setTheme(prev => {
            const next: SessionTheme = prev === 'university' ? 'obsidian' : prev === 'obsidian' ? 'parchment' : 'university'
            try { localStorage.setItem('edu_session_theme', next) } catch {}
            return next
        })
    }

    const messagesEndRef = useRef<HTMLDivElement>(null)
    const messagesContainerRef = useRef<HTMLDivElement>(null)
    const textareaRef = useRef<HTMLTextAreaElement>(null)
    const autoScrollRef = useRef(true) // true = user is parked at bottom -> follow output
    // Guard: loadMeta must only run once per categoryId+classId pair.
    // Without this, any profile re-render (auth refresh, webhook, etc.) re-fires
    // the effect, sets loadingMeta: true, and resets scroll to top mid-lesson.
    const metaLoadedForRef = useRef<string | null>(null)
    // Guard: one-shot initial scroll-to-bottom when history loads.
    // The streaming autoscroll only fires while isStreaming=true, so without
    // this a returning learner always lands at the top of their history.
    const didInitialScrollRef = useRef(false)

    // Scroll bubble visibility
    const [scrollPos, setScrollPos] = useState<'top' | 'middle' | 'bottom'>('bottom')

    const handleMessagesScroll = useCallback(() => {
        const el = messagesContainerRef.current
        if (!el) return
        const { scrollTop, scrollHeight, clientHeight } = el
        autoScrollRef.current = scrollHeight - scrollTop - clientHeight < 120

        if (scrollTop < 80) setScrollPos('top')
        else if (scrollTop + clientHeight >= scrollHeight - 80) setScrollPos('bottom')
        else setScrollPos('middle')
    }, [])

    const scrollToTop = useCallback(() => {
        messagesContainerRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
    }, [])

    const scrollToBottom = useCallback(() => {
        autoScrollRef.current = true
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, [])

    // Education session hook — THE ONLY hook for AI interaction
    const {
        messages, isLoading, isStreaming, error,
        quiz, isQuizOpen, quizPassed, currentUnit, currentLesson, masteryScore,
        sessionComplete, quizReady, sendMessage, requestQuiz, submitQuizAnswers, dismissQuiz, clearError,
        conversationId, suggestedChips, lastQuizResult,
        hasMoreMessages, isLoadingEarlier, loadEarlierMessages,
        currentConceptIndex, forceNextConcept
    } = useEducationSession(categoryId, classId)

    // Responsive sidebar
    useEffect(() => {
        const check = () => setSidebarOpen(window.innerWidth >= 1024)
        check()
        window.addEventListener('resize', check)
        return () => window.removeEventListener('resize', check)
    }, [])

    // Session timer
    useEffect(() => {
        const id = setInterval(() => setSessionSeconds(s => s + 1), 1000)
        return () => clearInterval(id)
    }, [])

    const formatTime = (s: number) => {
        const m = Math.floor(s / 60)
        const sec = s % 60
        return `${m}:${sec.toString().padStart(2, '0')}`
    }

    // Load category metadata + lesson plan
    useEffect(() => {
        if (!categoryId || !user || !userProfile) return
        // Only run once per categoryId+classId — re-renders from auth/profile
        // updates must not reset loadingMeta and scroll position mid-session.
        const metaKey = `${categoryId}::${classId ?? ''}`
        if (metaLoadedForRef.current === metaKey) return
        metaLoadedForRef.current = metaKey

        const loadMeta = async () => {
            setLoadingMeta(true)
            try {
                const { data: cat } = await supabase
                    .from('categories')
                    .select('name, icon, parent_id, category_type')
                    .eq('id', categoryId)
                    .single()

                if (cat) {
                    setCategoryName(cat.name || 'Learning Session')
                    setCategoryIcon(cat.icon || '📚')
                }

                // Load student progress for lesson plan
                let progQuery = supabase
                    .from('student_subject_progress')
                    .select('ai_generated_path, lessons_completed')
                    .eq('user_id', user.id)
                    .eq('category_id', categoryId)
                    .limit(1)
                if (classId) progQuery = progQuery.eq('class_id', classId)
                const { data: progress, error: progError } = await progQuery.maybeSingle() as unknown as { data: SubjectProgressData | null, error: any }

                if (progError) throw progError;

                // ── SUBSCRIPTION GATING (Single Source of Truth) ───────────────────
                const { isPaid, elite, standard } = getEntitlements(userProfile);

                // ONE class fetch — feeds the gate check, the lesson plan, essay mode,
                // and the elite/faith track detection below. (Was two separate awaits.)
                let cls: any = null
                if (classId) {
                    const { data: clsData } = await supabase
                        .from('classes')
                        .select('module_plan, essay_mode, curriculum_track, subject_group')
                        .eq('id', classId)
                        .maybeSingle();
                    cls = clsData
                    setEssayMode(cls?.essay_mode ?? null)
                    setEssayTrack(cls?.curriculum_track ?? '')
                }

                let isEliteClass = false;
                let isFaithClass = false;
                
                if (cls) {
                    if (cls.curriculum_track && ['foundation', 'intermediate', 'advanced'].includes(cls.curriculum_track)) {
                        isEliteClass = true;
                    }
                    if (cls.curriculum_track === 'faith' || cls.subject_group === 'Bible Journey' || cls.subject_group === 'Christian Studies') {
                        isFaithClass = true;
                    }
                }
                const isElite = isEliteClass || 
                                categoryId === 'e0000000-0000-0000-0000-000000000000' || 
                                cat?.parent_id === 'e0000000-0000-0000-0000-000000000000' ||
                                cat?.category_type === 'elite';

                // Platform (white-label) orgs: seats are the only meter
                // (reserve_facility_seat at invite time) — full catalog per seat.
                const isInstitutionalFacility = ['platform', 'school', 'facility', 'university', 'church', 'ngo'].includes(userProfile?.facility_type ?? '');
                const isOrgMember = !!userProfile?.facility_id && !userProfile.facility_id.startsWith('00000000-') && isInstitutionalFacility;
                const isPlatformOrg = userProfile?.facility_type === 'platform' || isOrgMember;
                if (isFaithClass && !isPaid && !isPlatformOrg) {
                    console.warn('[SessionGate] Faith curriculum requires an active paid plan.');
                    if (userProfile?.facility_role === 'member') {
                        navigate('/learn', { replace: true });
                    } else {
                        navigate('/subscription', { replace: true });
                    }
                    return;
                }

                if (isElite && !elite && !isPlatformOrg) {
                    console.warn('[SessionGate] Access denied: Elite Academy entitlement required.');
                    if (userProfile?.facility_role === 'member') {
                        navigate('/learn', { replace: true });
                    } else {
                        navigate('/homeschool/enrol?plan=edu_elite', { replace: true });
                    }
                    return;
                }

                // Prep subjects are free for everyone, or if specifically granted
                const isPrep = PREP_CATEGORY_IDS.includes(categoryId) || 
                              userProfile?.prep_foundation_access === true;

                if (!isElite && !isFaithClass && elite && !standard && !isPrep) {
                    console.warn('[SessionGate] Elite member attempting non-elite class without standard entitlement.');
                    navigate('/learn', { replace: true });
                    return;
                }

                if (!standard && !isPrep && !progress && !isPlatformOrg) {
                    // This is a new non-prep subject for a free user.
                    // Check if they already have one non-prep subject (the "1 free subject" slot).
                    const { count } = await supabase
                        .from('student_subject_progress')
                        .select('*', { count: 'exact', head: true })
                        .eq('user_id', user.id)
                        .not('category_id', 'in', `(${PREP_CATEGORY_IDS.join(',')})`)

                    if ((count ?? 0) >= 1) {
                        console.warn('[SessionGate] Free user exceeded 1 subject limit. Redirecting.');
                        if (userProfile?.facility_role === 'member') {
                            navigate('/learn', { replace: true });
                        } else {
                            navigate('/subscription', { replace: true });
                        }
                        return;
                    }
                }
                // ──────────────────────────────────────────────────────────────────



                if (classId) {
                    // Class-enrolled students: syllabus lives on the class row (already fetched)
                    if (cls?.module_plan && (cls.module_plan as any[]).length > 0) {
                        setLessonPlan(cls.module_plan as unknown as LessonPlan[])
                    }
                } else if (progress?.ai_generated_path) {
                    // Solo AI-generator learners: syllabus lives on the progress row
                    setLessonPlan(progress.ai_generated_path)
                }
                if (progress?.lessons_completed) {
                    setLessonsCompleted(progress.lessons_completed)
                }
            } catch (err) {
                console.error('[EDU_SESSION] Meta load error:', err)
            } finally {
                setLoadingMeta(false)
            }
        }

        loadMeta()
    // supabase is stabilised via useRef — intentionally excluded from deps.
    // userProfile entitlements don't change mid-session; if they do the next
    // navigation will pick it up. metaLoadedForRef guards re-fires.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [categoryId, classId, user])
    
        // Reset homework when moving between lessons (so it doesn't show on "Lesson 1", then show again on "Lesson 2")
    useEffect(() => {
        setShowReflection(false)
    }, [currentLesson])
        // Get current lesson info for header and objective (dual-convention normalized)
    const currentMod = lessonPlan.find(m => Number(m.module_id) === Number(currentUnit) || String(m.module_id) === String(currentUnit)) || lessonPlan[currentUnit - 1]
    const currentLessonData = currentMod?.lessons?.find(l => Number(l.lesson_id) === Number(currentLesson) || String(l.lesson_id) === String(currentLesson)) || currentMod?.lessons?.[currentLesson - 1]
    const objective = currentLessonData?.learning_objective

    // Stable string forms for effect deps (objects change identity every render)
    const currentModTitle = currentMod?.title
    const currentLessonTitle = currentLessonData?.title
    const currentLessonConcepts = currentLessonData?.core_concepts?.join('§') ?? ''

    // ── Handle Module Milestones + Overseer + Homework Reflection ─────────────
    useEffect(() => {
        if (quizPassed) {
            // Trigger Academic Overseer to update profile
            const triggerOverseer = async () => {
                const { data: { session } } = await supabase.auth.getSession()
                if (!session?.access_token) return

                fetch(`${(supabase as any).supabaseUrl}/functions/v1/ai-sage-overseer`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'Authorization': `Bearer ${session.access_token}`,
                    },
                    body: JSON.stringify({
                        user_id: user?.id,
                        trigger: 'lesson_complete',
                        trigger_slug: classId || categoryId,
                        sage_source: 'homeschool',
                    }),
                }).catch(() => { })
            }
            triggerOverseer()

            // Module milestone celebration
            if (lastQuizResult?.module_completed) {
                setShowMilestone(true)
            }

            // Written reflection (homework): rule-based timing, content from the module
            if (lastQuizResult?.passed && classId && essayMode) {
                const isModEnd = lastQuizResult?.module_completed
                const moduleIdxById = lessonPlan.findIndex(m => Number(m.module_id) === Number(currentUnit))
                const isCourseFinal = isModEnd && lessonPlan.length > 0 &&
                    (moduleIdxById === lessonPlan.length - 1 || Number(currentUnit) === lessonPlan.length)

                const shouldOffer =
                    (essayMode === 'essay_per_module' && isModEnd) ||
                    (essayMode === 'short_answer_only') ||
                    (essayMode === 'capstone_only' && isCourseFinal)

                if (shouldOffer && currentModTitle) {
                    setReflectionPrompt(generateReflectionPrompt({
                        mode: essayMode,
                        subjectName: categoryName,
                        moduleTitle: currentModTitle,
                        moduleNumber: currentUnit,
                        lessonTitle: currentLessonTitle,
                        lessonNumber: currentLesson,
                        coreConcepts: currentLessonConcepts ? currentLessonConcepts.split('§') : [],
                        isFaithTrack: false,
                    }))
                    setShowReflection(true)
                }
            }
        }
    }, [quizPassed, lastQuizResult?.module_completed, lastQuizResult?.passed, supabase, user?.id, classId, categoryId, essayMode, lessonPlan, categoryName, currentUnit, currentLesson, currentModTitle, currentLessonTitle, currentLessonConcepts])

    // ── Handle ?reflection=1 direct link (homework re-entry from /learn) ─────
    useEffect(() => {
        if (searchParams.get('reflection') !== '1' || !essayMode || !user?.id) return
        if (!classId) return
        const targetMod = Number(searchParams.get('module') || searchParams.get('unit')) || currentUnit
        supabase
            .from('university_submissions')
            .select('id')
            .eq('user_id', user.id)
            .eq('class_id', classId)
            .eq('module_id', targetMod)
            .maybeSingle()
            .then(({ data: sub }) => {
                if (!sub) {
                    const targetModPlan = lessonPlan.find(m => Number(m.module_id) === targetMod || String(m.module_id) === String(targetMod))
                    const targetModTitle = targetModPlan?.title || currentModTitle || `Module ${targetMod}`
                    const firstLesson = targetModPlan?.lessons?.[0]
                    setReflectionPrompt(generateReflectionPrompt({
                        mode: essayMode,
                        subjectName: categoryName,
                        moduleTitle: targetModTitle,
                        moduleNumber: targetMod,
                        lessonTitle: firstLesson?.title || currentLessonTitle,
                        lessonNumber: firstLesson?.lesson_id || currentLesson,
                        coreConcepts: firstLesson?.core_concepts || (currentLessonConcepts ? currentLessonConcepts.split('§') : []),
                        isFaithTrack: false,
                    }))
                    setShowReflection(true)
                }
            })
    }, [searchParams, essayMode, classId, user?.id, currentUnit, currentLesson, lessonPlan, categoryName, currentModTitle, currentLessonTitle, currentLessonConcepts])
    // Follow output ONLY while auto-scroll is enabled AND still streaming;
    // keep the reader anchored otherwise (top-pinning behaviour)
    useEffect(() => {
        if (isStreaming && autoScrollRef.current) {
            messagesEndRef.current?.scrollIntoView({ block: 'end' })
        }
    }, [messages, isStreaming])

    // Initial scroll-to-bottom for returning learners.
    // Fires once when history arrives (messages.length > 0, not streaming, not loading).
    // Skipped for fresh sessions (messages.length === 0) — auto-start + streaming
    // scroll handles those naturally. Uses instant scroll (no smooth) so the
    // learner is positioned correctly before the pill/jump-button machinery takes over.
    useEffect(() => {
        if (didInitialScrollRef.current) return
        if (!conversationId || isLoading || isStreaming || messages.length === 0) return
        didInitialScrollRef.current = true
        requestAnimationFrame(() => {
            const el = messagesContainerRef.current
            if (el) el.scrollTo({ top: el.scrollHeight })
        })
    }, [conversationId, messages.length, isLoading, isStreaming])

    // Auto-start the first lesson when conversation is ready but no messages exist
    useEffect(() => {
        if (conversationId && messages.length === 0 && !isLoading && !isStreaming && !hasAutoStarted && !loadingMeta && categoryName) {
            setHasAutoStarted(true)
            sendMessage('start the lesson')
        }
    }, [conversationId, messages.length, isLoading, isStreaming, hasAutoStarted, loadingMeta, categoryName, sendMessage])

    // Input handling
    const handleSend = useCallback(async () => {
        if (!userInput.trim() || isLoading || isStreaming) return
        const text = userInput.trim()
        setUserInput('')
        if (textareaRef.current) textareaRef.current.style.height = 'auto'
        await sendMessage(text)
    }, [userInput, isLoading, isStreaming, sendMessage])

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault()
            handleSend()
        }
    }

    const adjustTextarea = () => {
        const ta = textareaRef.current
        if (ta) {
            ta.style.height = 'auto'
            ta.style.height = `${Math.min(ta.scrollHeight, 140)}px`
        }
    }

    // Auth guard
    if (authLoading || (user && loadingMeta)) {
        return (
            <div style={{
            height: '100%', flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center',
                background: '#0d1520', flexDirection: 'column', gap: 16
            }}>
                <motion.div
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1.5, repeat: Infinity, ease: 'linear' }}
                >
                    <Loader2 size={36} style={{ color: T.studyBlue }} />
                </motion.div>
                <p style={{ color: T.slate, fontFamily: T.fontBody, fontSize: 15 }}>
                    Preparing your classroom...
                </p>
            </div>
        )
    }

    if (!user) {
        return <Navigate to="/auth/login" replace />
    }

    if (!categoryId) {
        return <Navigate to="/learn" replace />
    }


    return (
        <div className="edu-session-root" style={{
            flex: 1, height: '100%', display: 'flex', overflow: 'hidden',
            background: currentTheme.outerBg, fontFamily: T.fontBody
        }}>
            <style>{`
                .no-scrollbar::-webkit-scrollbar { display: none; }
                .no-scrollbar { -ms-overflow-style: none; scrollbar-width: none; }
            `}</style>

            {/* ── Curriculum Sidebar ── */}
            {lessonPlan.length > 0 && (
                <CurriculumSidebar
                    plan={lessonPlan}
                    currentUnit={currentUnit}
                    currentLesson={currentLesson}
                    masteryScore={masteryScore}
                    categoryName={categoryName}
                    categoryIcon={categoryIcon}
                    isOpen={sidebarOpen}
                    onClose={() => setSidebarOpen(false)}
                    lessonsCompleted={lessonsCompleted}
                    currentConcepts={currentLessonData?.core_concepts || []}
                    currentConceptIndex={currentConceptIndex}
                    showForceControl={!sessionComplete && !isQuizOpen && ((userProfile?.facility_type === 'homeschool' || userProfile?.facility_type === 'family_edu') ? !!((userProfile?.preferences as any)?.guide_mode ?? (userProfile?.preferences as any)?.parent_guide) : true)}
                    onForceNextConcept={forceNextConcept}
                />
            )}

            {/* ── Main Panel ── */}
            <div style={{
                flex: 1,
                display: 'flex',
                flexDirection: 'column',
                minWidth: 0,
                height: '100%',
                overflow: 'hidden',
                minHeight: 0
            }}>

                {/* ── Premium Header Bar ── */}
                <motion.header
                    className="edu-header"
                    style={{
                        flexShrink: 0,
                        height: typeof window !== 'undefined' && window.innerWidth < 640 ? 56 : 64,
                        background: currentTheme.headerBg,
                        borderBottom: '1px solid rgba(255,255,255,0.08)',
                        boxShadow: '0 2px 24px rgba(0,0,0,0.3)',
                        display: 'flex', alignItems: 'center', padding: '0 16px', gap: 10,
                        zIndex: 10, position: 'relative'
                    }}
                    initial={{ opacity: 0 }}
                    animate={{ opacity: 1 }}
                >
                    <div className="edu-header-row1">
                    {/* Sidebar toggle / Back */}
                    <button onClick={() => lessonPlan.length > 0 ? setSidebarOpen(!sidebarOpen) : setShowExitConfirm(true)} style={{
                        width: 40, height: 40, borderRadius: 10, border: 'none',
                        background: 'rgba(255,255,255,0.06)', cursor: 'pointer',
                        color: 'rgba(255,255,255,0.7)', display: 'flex',
                        alignItems: 'center', justifyContent: 'center'
                    }}>
                        {lessonPlan.length > 0 ? <Menu size={18} /> : <ArrowLeft size={18} />}
                    </button>

                    {/* Subject info */}
                    <div style={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 10 }}>
                        <div style={{
                            width: 36, height: 36, borderRadius: 10, flexShrink: 0,
                            background: 'rgba(59,91,219,0.2)', border: '1px solid rgba(59,91,219,0.3)',
                            display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 18
                        }}>{categoryIcon}</div>
                        <div style={{ minWidth: 0 }}>
                            <div className="edu-subject-name" style={{
                                fontFamily: T.fontDisplay, fontSize: 15, fontWeight: 700,
                                color: T.white, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
                            }}>{categoryName}</div>
                            <div className="edu-lesson-line" style={{ display: 'flex', alignItems: 'center', gap: 4, marginTop: 1 }}>
                                <BookOpen size={10} style={{ color: T.sageMid }} />
                                <span style={{ fontSize: 11, color: T.sageMid }}>
                                    Unit {currentUnit}
                                </span>
                                <ChevronRight size={10} style={{ color: 'rgba(255,255,255,0.2)' }} />
                                <span style={{ fontSize: 11, color: T.sageMid }}>
                                    {currentLessonData?.title || `Lesson ${currentLesson}`}
                                </span>
                                {isStreaming && (
                                    <motion.div style={{ display: 'flex', gap: 2, marginLeft: 4 }}>
                                        {[0, 0.12, 0.24].map((d, i) => (
                                            <motion.div key={i} style={{ width: 4, height: 4, borderRadius: '50%', background: T.sageMid }}
                                                animate={{ y: [0, -3, 0] }}
                                                transition={{ duration: 0.8, delay: d, repeat: Infinity }}
                                            />
                                        ))}
                                    </motion.div>
                                )}
                            </div>
                        </div>
                    </div>
                    </div>
                    <div className="edu-header-row2">
                    {/* Theme switcher */}
                    <button
                        onClick={cycleTheme}
                        className="edu-theme-toggle"
                        style={{
                            display: 'flex', alignItems: 'center', gap: 5,
                            padding: '5px 10px', borderRadius: 8,
                            border: '1px solid rgba(255,255,255,0.12)',
                            background: 'rgba(255,255,255,0.06)',
                            cursor: 'pointer',
                        }}
                        title={`Theme: ${currentTheme.name}. Click to cycle.`}
                    >
                        <Palette size={11} style={{ color: T.gold }} />
                        <span className="edu-theme-name" style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.75)' }}>{currentTheme.name}</span>
                    </button>

                    {/* Guide Mode toggle */}
                    <button
                        onClick={async () => {
                            const prefs = { ...((userProfile?.preferences as Record<string, any>) || {}) }
                            const nextVal = !(prefs.guide_mode ?? prefs.parent_guide)
                            await updateProfile({ preferences: { ...prefs, guide_mode: nextVal, parent_guide: nextVal } as any })
                        }}
                        className="edu-parent-guide-toggle"
                        style={{
                            display: 'flex', alignItems: 'center', gap: 4,
                            padding: '7px 10px', borderRadius: 8,
                            border: '1px solid rgba(255,255,255,0.12)',
                            background: (userProfile?.preferences as any)?.guide_mode || (userProfile?.preferences as any)?.parent_guide ? 'rgba(212,168,67,0.25)' : 'rgba(255,255,255,0.06)',
                            cursor: 'pointer',
                        }}
                        title="Guided mode: one teaching prompt per message, for a parent or guide facilitating this session"
                        aria-pressed={(userProfile?.preferences as any)?.guide_mode || (userProfile?.preferences as any)?.parent_guide ? 'true' : 'false'}
                    >
                        <Users size={11} style={{ color: T.sageMid }} />
                        <span className="edu-guide-label" style={{ fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.8)' }}>{t('education.guide_mode.short', 'Guided')}</span>
                        <span className="edu-guide-state" style={{
                            fontSize: 9, fontWeight: 800, letterSpacing: '0.05em', textTransform: 'uppercase',
                            color: (userProfile?.preferences as any)?.guide_mode || (userProfile?.preferences as any)?.parent_guide ? '#d4a843' : 'rgba(255,255,255,0.35)'
                        }}>
                            {(userProfile?.preferences as any)?.guide_mode || (userProfile?.preferences as any)?.parent_guide ? t('common.on', 'On') : t('common.off', 'Off')}
                        </span>
                    </button>

                    <div className="edu-language-picker-wrapper" style={{ marginLeft: 8 }}>
                        <LanguagePicker />
                    </div>

                    {/* Session timer — desktop only */}
                    <div className="edu-timer-pill" style={{
                        display: 'flex', alignItems: 'center', gap: 5,
                        background: 'rgba(255,255,255,0.06)',
                        border: '1px solid rgba(255,255,255,0.08)',
                        borderRadius: 8, padding: '5px 10px'
                    }}>
                        <Clock size={11} style={{ color: T.sageMid }} />
                        <span style={{ fontSize: 12, fontWeight: 600, color: 'rgba(255,255,255,0.55)', fontVariantNumeric: 'tabular-nums' }}>
                            {formatTime(sessionSeconds)}
                        </span>
                    </div>

                    {/* Mastery + Exit */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexShrink: 0 }}>
                        <MasteryRing score={masteryScore} size={typeof window !== 'undefined' && window.innerWidth < 640 ? 34 : 44} />
                        <motion.button
                            onClick={() => setShowExitConfirm(true)}
                            style={{
                                display: 'flex', alignItems: 'center', gap: 4,
                                padding: '7px 12px', borderRadius: 10,
                                border: '1px solid rgba(255,255,255,0.1)',
                                background: 'rgba(255,255,255,0.06)',
                                color: 'rgba(255,255,255,0.6)',
                                fontFamily: T.fontBody, fontSize: 13, fontWeight: 600,
                                cursor: 'pointer'
                            }}
                            whileHover={{ background: 'rgba(255,255,255,0.12)', color: T.white }}
                            whileTap={{ scale: 0.96 }}
                        >
                            <X size={14} />
                            <span className="edu-exit-label">{t('education.session.exit.exit', 'Exit')}</span>
                        </motion.button>
                    </div>
                    </div>
                </motion.header>

                {/* ── Lesson Objective Banner ── */}
                {objective && (
                    <motion.div
                        initial={{ opacity: 0, height: 0 }}
                        animate={{ opacity: 1, height: 'auto' }}
                        style={{
                            flexShrink: 0, background: currentTheme.objectiveBg,
                            padding: '10px 16px', borderBottom: currentTheme.objectiveBorder,
                            display: 'flex', alignItems: 'center', gap: 10,
                            overflow: 'hidden'
                        }}
                    >
                        <div style={{
                            width: 22, height: 22, borderRadius: 6, background: T.sage,
                            display: 'flex', alignItems: 'center', justifyContent: 'center',
                            color: T.white, flexShrink: 0
                        }}>
                            <Info size={14} />
                        </div>
                        <div style={{ flex: 1, minWidth: 0 }}>
                            <span style={{
                                fontSize: 11, fontWeight: 800, color: currentTheme.objectiveLabelColor,
                                textTransform: 'uppercase', letterSpacing: '0.04em'
                            }}>{t('education.session.objective', 'Objective')}</span>
                            <p style={{
                                fontSize: typeof window !== 'undefined' && window.innerWidth < 640 ? 12 : 13,
                                fontWeight: 600, color: currentTheme.objectiveTextColor,
                                margin: 0, whiteSpace: typeof window !== 'undefined' && window.innerWidth < 640 ? 'normal' : 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'
                            }}>{objective}</p>
                        </div>
                    </motion.div>
                )}

                {/* ── Messages Area ── */}
                <div
                    ref={messagesContainerRef}
                    onScroll={handleMessagesScroll}
                    style={{ flex: 1, overflowY: 'auto', position: 'relative', minHeight: 0, WebkitOverflowScrolling: 'touch' }}
                >
                    <div style={{ maxWidth: 860, margin: '0 auto', padding: '12px 12px 40px' }} className="edu-messages-inner">

                        {/* Empty state */}
                        {messages.length === 0 && !isLoading && (
                            <motion.div
                                initial={{ opacity: 0, y: 20 }}
                                animate={{ opacity: 1, y: 0 }}
                                style={{ textAlign: 'center', padding: '48px 0' }}
                            >
                                <motion.div
                                    style={{
                                        width: 72, height: 72, margin: '0 auto 20px',
                                        background: `linear-gradient(135deg, ${T.studyBlue}, ${T.studyBlueDark})`,
                                        borderRadius: 20, display: 'flex', alignItems: 'center', justifyContent: 'center',
                                        boxShadow: '0 12px 32px rgba(59,91,219,0.3)'
                                    }}
                                    animate={{ y: [0, -4, 0] }}
                                    transition={{ duration: 3, repeat: Infinity, ease: 'easeInOut' }}
                                >
                                    <GraduationCap size={36} style={{ color: T.white }} />
                                </motion.div>
                                <h2 style={{ fontFamily: T.fontDisplay, fontSize: 26, fontWeight: 700, color: T.navy, margin: '0 0 10px' }}>
                                    {categoryName}
                                </h2>
                                <p style={{ color: T.slate, fontSize: 15, lineHeight: 1.6 }}>
                                    {t('education.session.preparing', 'Your personal tutor is preparing your lesson...')}
                                </p>
                            </motion.div>
                        )}

                        {/* Load earlier messages button */}
                        {hasMoreMessages && (
                            <div style={{ textAlign: 'center', margin: '8px 0 16px' }}>
                                <button
                                    onClick={loadEarlierMessages}
                                    disabled={isLoadingEarlier}
                                    style={{
                                        padding: '6px 14px',
                                        borderRadius: 20,
                                        border: '1px solid #e2e8f0',
                                        background: '#fff',
                                        color: '#64748b',
                                        fontSize: 13,
                                        fontWeight: 600,
                                        cursor: isLoadingEarlier ? 'default' : 'pointer',
                                        boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
                                    }}
                                >
                                    {isLoadingEarlier ? t('education.session.loadingEarlier', 'Loading earlier messages...') : t('education.session.loadEarlier', '↑ Load earlier messages')}
                                </button>
                            </div>
                        )}

                        {/* Message list */}
                        {messages.map((msg, i) => (
                            <MessageBubble key={msg.id} message={msg} theme={theme} />
                        ))}

                        {/* Typing indicator */}
                        {(isLoading || isStreaming) && messages.length > 0 && messages[messages.length - 1]?.role === 'user' && (
                            <TypingIndicator />
                        )}

                        {/* Error banner */}
                        {error && (
                            <motion.div
                                initial={{ opacity: 0, y: 8 }}
                                animate={{ opacity: 1, y: 0 }}
                                style={{
                                    margin: '12px 0', padding: '12px 16px', borderRadius: 12,
                                    background: '#fee2e2', border: '1px solid #fecaca',
                                    color: '#991b1b', fontSize: 14,
                                    display: 'flex', alignItems: 'center', justifyContent: 'space-between'
                                }}
                            >
                                <span>{error}</span>
                                <button onClick={clearError} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#991b1b', padding: 4 }}>✕</button>
                            </motion.div>
                        )}

                        {/* New content pill when user is scrolled up during streaming */}
                        <AnimatePresence>
                            {!autoScrollRef.current && isStreaming && (
                                <motion.div
                                    initial={{ opacity: 0, y: 10 }}
                                    animate={{ opacity: 1, y: 0 }}
                                    exit={{ opacity: 0, y: 10 }}
                                    style={{
                                        position: 'sticky',
                                        bottom: 16,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        zIndex: 25,
                                        pointerEvents: 'none'
                                    }}
                                >
                                    <button
                                        onClick={scrollToBottom}
                                        style={{
                                            pointerEvents: 'auto',
                                            display: 'flex',
                                            alignItems: 'center',
                                            gap: 6,
                                            padding: '8px 16px',
                                            borderRadius: 20,
                                            background: T.navy,
                                            color: '#fff',
                                            fontSize: 13,
                                            fontWeight: 600,
                                            border: '1px solid rgba(255,255,255,0.2)',
                                            boxShadow: '0 4px 16px rgba(0,0,0,0.3)',
                                            cursor: 'pointer',
                                        }}
                                    >
                                        <span>↓ New content</span>
                                    </button>
                                </motion.div>
                            )}
                        </AnimatePresence>

                                          {/* Written reflection (homework mode) */}
                        {showReflection && classId && (
                            <div className="edu-reflection-wrap" style={{ marginTop: 20, marginBottom: 24, paddingLeft: 48, paddingRight: 16 }}>
                                <ShortAnswerBox
                                    prompt={reflectionPrompt}
                                    classId={classId}
                                    moduleId={currentUnit}
                                    lessonId={currentLesson}
                                    sageSource={essayTrack === 'foundation' || essayTrack === 'intermediate' || essayTrack === 'advanced' ? 'elite' : 'homeschool'}
                                    accentColor={T.gold}
                                    wordMin={50}
                                    wordMax={300}
                                />
                            </div>
                        )}

                        <div ref={messagesEndRef} style={{ height: 40 }} />
                    </div>

                    {/* ── Scroll Bubble Buttons ── */}
                    <AnimatePresence>
                        {scrollPos !== 'top' && (
                            <motion.button
                                key="scroll-up"
                                initial={{ opacity: 0, scale: 0.7 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.7 }}
                                transition={{ duration: 0.18 }}
                                onClick={scrollToTop}
                                title="Scroll to top"
                                style={{
                                    position: 'sticky', top: 12,
                                    float: 'right', marginRight: 12, marginBottom: 0,
                                    width: 36, height: 36, borderRadius: '50%',
                                    background: 'rgba(59,91,219,0.9)',
                                    border: 'none', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    boxShadow: '0 4px 14px rgba(59,91,219,0.35)',
                                    zIndex: 20, flexShrink: 0,
                                    backdropFilter: 'blur(8px)',
                                }}
                            >
                                <ChevronUp size={18} color="#fff" />
                            </motion.button>
                        )}
                    </AnimatePresence>

                    {/* Milestone Celebration */}
                    <AnimatePresence>
                        {showMilestone && (
                            <ModuleMilestone
                                isOpen={showMilestone}
                                moduleTitle={currentMod?.title || `Module ${currentUnit}`}
                                className={categoryName}
                                accentColor={T.studyBlue}
                                variant="homeschool"
                                dashboardPath="/learn"
                                onClose={() => setShowMilestone(false)}
                            />
                        )}
                    </AnimatePresence>

                    {/* Scroll-to-bottom bubble */}
                    <AnimatePresence>
                        {scrollPos !== 'bottom' && (
                            <motion.button
                                key="scroll-down"
                                initial={{ opacity: 0, scale: 0.7 }}
                                animate={{ opacity: 1, scale: 1 }}
                                exit={{ opacity: 0, scale: 0.7 }}
                                transition={{ duration: 0.18 }}
                                onClick={scrollToBottom}
                                title="Scroll to bottom"
                                style={{
                                    position: 'sticky', bottom: 16,
                                    float: 'right', marginRight: 12,
                                    width: 36, height: 36, borderRadius: '50%',
                                    background: 'rgba(59,91,219,0.9)',
                                    border: 'none', cursor: 'pointer',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                    boxShadow: '0 4px 14px rgba(59,91,219,0.35)',
                                    zIndex: 20, flexShrink: 0,
                                    backdropFilter: 'blur(8px)',
                                }}
                            >
                                <ChevronDown size={18} color="#fff" />
                            </motion.button>
                        )}
                    </AnimatePresence>
                </div>

                {/* ── Input Area ── */}
                <div style={{
                    flexShrink: 0, background: currentTheme.headerBg,
                    borderTop: `1px solid rgba(255,255,255,0.07)`,
                    padding: '12px 16px',
                    paddingBottom: 'calc(14px + env(safe-area-inset-bottom))'
                }}>
                    {/* Quick actions — Now ABOVE the input box */}
                    {messages.length > 0 && !isLoading && !isStreaming && (
                        <motion.div
                            initial={{ opacity: 0, y: 6 }}
                            animate={{ opacity: 1, y: 0 }}
                            style={{
                                maxWidth: 860, margin: '0 auto 12px',
                                display: 'flex', gap: 6, flexWrap: 'nowrap',
                                overflowX: 'auto', paddingBottom: 4,
                                msOverflowStyle: 'none', scrollbarWidth: 'none'
                            }}
                            className="no-scrollbar"
                        >
                            {(suggestedChips.length > 0
                                ? suggestedChips
                                : (quizPassed
                                    ? [t('education.session.chips.startNext', 'Start Next Lesson'), t('education.session.chips.reviewSummary', 'Review lesson summary'), t('education.session.chips.followUp', 'Ask follow up question')]
                                    : sessionComplete
                                        ? [t('education.session.quiz', 'Start Quiz'), t('education.session.chips.moreDetails', 'I need more details'), t('education.session.chips.reviewSummary', 'Review lesson summary')]
                                        : [t('education.session.chips.continue', 'Continue'), t('education.session.chips.explainDifferently', 'Can you explain that differently?'), t('education.session.chips.giveExample', 'Give me an example')]
                                )
                            ).map(chip => (
                                <button
                                    key={chip}
                                    disabled={quizReady || isLoading || isStreaming}
                                    onClick={() => {
                                        if (chip === 'Start Quiz') {
                                            requestQuiz()
                                        } else {
                                            sendMessage(chip)
                                        }
                                    }}
                                    style={{
                                        padding: '7px 16px', borderRadius: 20,
                                        borderWidth: '1px',
                                        borderStyle: 'solid',
                                        borderColor: (chip === 'Start Quiz' || chip === 'Start Next Lesson') ? T.studyBlue : currentTheme.chipBorder,
                                        background: (chip === 'Start Quiz' || chip === 'Start Next Lesson') ? T.studyBlue : currentTheme.chipBg,
                                        color: (chip === 'Start Quiz' || chip === 'Start Next Lesson') ? T.white : currentTheme.chipTextColor,
                                        fontSize: 13, fontWeight: 700, cursor: 'pointer',
                                        fontFamily: T.fontBody, transition: 'all 0.2s',
                                        whiteSpace: 'nowrap', flexShrink: 0,
                                        boxShadow: (chip === 'Start Quiz' || chip === 'Start Next Lesson') ? '0 4px 12px rgba(59,91,219,0.15)' : '0 2px 6px rgba(0,0,0,0.03)'
                                    }}
                                    onMouseEnter={(e) => {
                                        const el = e.currentTarget as HTMLButtonElement;
                                        if (chip !== 'Start Quiz' && chip !== 'Start Next Lesson') {
                                            el.style.borderColor = currentTheme.chipHoverBorder;
                                            el.style.color = currentTheme.chipHoverTextColor;
                                            el.style.boxShadow = '0 4px 12px rgba(59,91,219,0.12)';
                                        } else {
                                            el.style.filter = 'brightness(1.1)';
                                            el.style.transform = 'translateY(-1px)';
                                            el.style.boxShadow = '0 6px 16px rgba(59,91,219,0.25)';
                                        }
                                    }}
                                    onMouseLeave={(e) => {
                                        const el = e.currentTarget as HTMLButtonElement;
                                        if (chip !== 'Start Quiz' && chip !== 'Start Next Lesson') {
                                            el.style.borderColor = currentTheme.chipBorder;
                                            el.style.color = currentTheme.chipTextColor;
                                            el.style.boxShadow = '0 2px 6px rgba(0,0,0,0.05)';
                                        } else {
                                            el.style.filter = 'none';
                                            el.style.transform = 'none';
                                            el.style.boxShadow = '0 4px 12px rgba(59,91,219,0.15)';
                                        }
                                    }}
                                >{chip}</button>
                            ))}
                        </motion.div>
                    )}

                    <div style={{
                        maxWidth: 860, margin: '0 auto',
                        display: 'flex', gap: 10, alignItems: 'center'
                    }}>
                        <div style={{
                            flex: 1, background: currentTheme.inputBg, borderRadius: 18,
                            padding: '4px 16px',
                            display: 'flex', alignItems: 'center', gap: 8,
                            transition: 'border-color 0.2s',
                            borderWidth: '2px',
                            borderStyle: 'solid',
                            borderColor: userInput.trim() ? T.studyBlue : currentTheme.inputBorder
                        }}>
                            <textarea
                                ref={textareaRef}
                                value={userInput}
                                onChange={(e) => { setUserInput(e.target.value); adjustTextarea() }}
                                onKeyDown={handleKeyDown}
                                placeholder={t('education.session.inputPlaceholder', "Ask a question or type 'continue'...")}
                                style={{
                                    flex: 1, background: 'transparent', border: 'none',
                                    outline: 'none', padding: '10px 0', fontSize: 16,
                                    color: currentTheme.inputTextColor, fontFamily: T.fontBody, minHeight: 42,
                                    maxHeight: 140, resize: 'none', lineHeight: 1.5
                                }}
                                rows={1}
                            />
                        </div>
                        <motion.button
                            onClick={handleSend}
                            disabled={!userInput.trim() || isLoading || isStreaming}
                            style={{
                                width: 44, height: 44, borderRadius: 14,
                                background: userInput.trim()
                                    ? `linear-gradient(135deg, ${T.studyBlue}, ${T.studyBlueDark})`
                                    : T.creamDark,
                                color: T.white, border: 'none', cursor: 'pointer',
                                display: 'flex', alignItems: 'center', justifyContent: 'center',
                                boxShadow: userInput.trim() ? '0 4px 16px rgba(59,91,219,0.3)' : 'none',
                                transition: 'all 0.2s',
                                flexShrink: 0
                            }}
                            whileHover={userInput.trim() ? { scale: 1.05 } : {}}
                            whileTap={userInput.trim() ? { scale: 0.95 } : {}}
                        >
                            <Send size={18} />
                        </motion.button>
                    </div>
                </div>
            </div>

            {/* ── Quiz Overlay ── */}
            {
                isQuizOpen && quiz && (
                    <QuizInterface
                        isOpen={isQuizOpen}
                        quiz={quiz}
                        onSubmit={submitQuizAnswers}
                        onDismiss={dismissQuiz}
                        categoryName={categoryName}
                        lastQuizResult={lastQuizResult}
                    />
                )
            }

            {/* ── Exit Confirmation Modal ── */}
            <AnimatePresence>
                {showExitConfirm && (
                    <motion.div
                        style={{
                            position: 'fixed', inset: 0, zIndex: 200,
                            display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16
                        }}
                        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
                    >
                        <motion.div style={{ position: 'absolute', inset: 0, background: 'rgba(26,39,64,0.6)' }}
                            onClick={() => setShowExitConfirm(false)} />
                        <motion.div
                            style={{
                                position: 'relative', background: T.white, borderRadius: 24,
                                padding: '32px 28px', maxWidth: 380, width: '100%',
                                boxShadow: '0 24px 80px rgba(26,39,64,0.3)',
                                border: `1px solid ${T.sageLight}`
                            }}
                            initial={{ scale: 0.9, y: 20 }}
                            animate={{ scale: 1, y: 0 }}
                            exit={{ scale: 0.95, opacity: 0 }}
                            transition={{ type: 'spring', stiffness: 340, damping: 28 }}
                        >
                            <div style={{ fontSize: 36, textAlign: 'center', marginBottom: 16 }}>📚</div>
                            <h3 style={{
                                fontFamily: T.fontDisplay, fontSize: 22, fontWeight: 700,
                                color: T.navy, textAlign: 'center', marginBottom: 10
                            }}>{t('education.session.exit.title', 'Leave this lesson?')}</h3>
                            <p style={{
                                fontSize: 14, color: T.slate, textAlign: 'center',
                                lineHeight: 1.7, marginBottom: 24
                            }}>
                                {t('education.session.exit.subtitle', 'Your progress is saved automatically. You can pick up exactly where you left off.')}
                            </p>
                            <div style={{ display: 'flex', gap: 10 }}>
                                <button onClick={() => setShowExitConfirm(false)} style={{
                                    flex: 1, padding: 13, borderRadius: 14,
                                    border: `1px solid ${T.sageLight}`, background: T.sageLight,
                                    color: T.sage, fontFamily: T.fontBody, fontSize: 15,
                                    fontWeight: 700, cursor: 'pointer'
                                }}>{t('education.session.exit.keepLearning', 'Keep Learning')}</button>
                                <button onClick={() => navigate('/learn')} style={{
                                    flex: 1, padding: 13, borderRadius: 14,
                                    border: 'none', background: T.navy,
                                    color: T.white, fontFamily: T.fontBody, fontSize: 15,
                                    fontWeight: 700, cursor: 'pointer'
                                }}>{t('education.session.exit.exit', 'Exit')}</button>
                            </div>
                        </motion.div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* ── Quiz Preparing Overlay ── */}
            <AnimatePresence>
                {quizReady && <QuizPreparing />}
            </AnimatePresence>

            {/* Responsive CSS â€” prose-sage uses currentTheme so bold/headings/bullets always contrast */}
            <style>{`
        @media (max-width: 767px) {
          .edu-timer-pill, .edu-exit-label, .edu-theme-toggle { display: none !important; }
          .edu-messages-inner { padding: 8px 8px 32px !important; }
          .edu-bubble { padding: 12px 14px !important; font-size: 15px !important; max-width: 90% !important; }
          .edu-parent-guide-toggle { padding: 5px 7px !important; }
          .edu-language-picker-wrapper { margin-left: 4px !important; }
        }
        @media (max-width: 480px) {
          .edu-bubble { max-width: 95% !important; font-size: 14px !important; }
          .edu-messages-inner { padding: 6px 6px 28px !important; }
        }
        .edu-header-row1, .edu-header-row2 { display: contents; }
        @media (max-width: 640px) {
          .edu-header { flex-direction: column !important; align-items: stretch !important; height: auto !important; padding: 8px 10px !important; gap: 4px !important; }
          .edu-header-row1 { display: flex !important; align-items: center !important; gap: 10px !important; width: 100% !important; }
          .edu-header-row2 { display: flex !important; align-items: center !important; justify-content: space-between !important; gap: 6px !important; width: 100% !important; }
          .edu-language-picker-wrapper { margin-left: 0 !important; }
          .edu-subject-name { font-size: 11px !important; color: rgba(255,255,255,0.5) !important; }
          .edu-lesson-line { margin-top: 3px !important; }
          .edu-lesson-line span:first-of-type { font-size: 11px !important; color: rgba(255,255,255,0.45) !important; }
          .edu-lesson-line span:last-of-type { font-size: 14px !important; font-weight: 700 !important; color: rgba(255,255,255,0.95) !important; }
          .edu-theme-name { max-width: 72px !important; overflow: hidden !important; text-overflow: ellipsis !important; white-space: nowrap !important; }
          .edu-guide-state { padding: 1px 5px !important; border-radius: 6px !important; background: rgba(255,255,255,0.08) !important; }
          .edu-reflection-wrap { padding-left: 8px !important; padding-right: 8px !important; }
        }
        .edu-session-root { -webkit-tap-highlight-color: transparent; }
        .edu-session-root button { touch-action: manipulation; }
        .prose-sage { color: ${currentTheme.proseColor}; }
        .prose-sage h1, .prose-sage h2, .prose-sage h3 {
          font-family: ${T.fontDisplay};
          color: ${currentTheme.proseHeadingColor};
          margin: 16px 0 8px;
        }
        .prose-sage h2 { font-size: 20px; }
        .prose-sage h3 { font-size: 17px; }
        .prose-sage p { margin: 8px 0; color: ${currentTheme.proseColor}; }
        .prose-sage ul, .prose-sage ol { margin: 8px 0; padding-left: 20px; color: ${currentTheme.proseColor}; }
        .prose-sage li { margin: 4px 0; color: ${currentTheme.proseColor}; }
        .prose-sage strong { color: ${currentTheme.proseStrongColor}; font-weight: 700; }
        .prose-sage em { color: ${currentTheme.proseColor}; opacity: 0.9; }
        .prose-sage code { background: rgba(255,255,255,0.08); color: ${currentTheme.proseStrongColor}; padding: 2px 5px; border-radius: 4px; font-size: 14px; }
        .prose-sage blockquote { border-left: 3px solid ${T.gold}; padding-left: 14px; margin: 12px 0; color: ${currentTheme.proseColor}; opacity: 0.85; font-style: italic; }
        .prose-sage table { width: 100%; border-collapse: collapse; margin: 14px 0; font-size: 14px; border-radius: 8px; overflow: hidden; border: 1px solid rgba(255,255,255,0.1); }
        .prose-sage th { background: rgba(255,255,255,0.08); color: ${currentTheme.proseHeadingColor}; font-weight: 700; text-align: left; padding: 10px 14px; border-bottom: 2px solid rgba(255,255,255,0.12); }
        .prose-sage td { padding: 9px 14px; border-bottom: 1px solid rgba(255,255,255,0.06); color: ${currentTheme.proseColor}; }
        .prose-sage tr:nth-child(even) td { background: rgba(255,255,255,0.03); }
        .prose-sage tr:last-child td { border-bottom: none; }
        .prose-sage a { color: ${T.gold}; text-decoration: underline; }
        textarea::placeholder { color: ${currentTheme.inputPlaceholder}; }
      `}</style>
        </div>
    )
}
